"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsxs, jsx } from 'react/jsx-runtime';
import { SvgIcon } from '../../../SvgIcon/index.mjs';
import { SvgIconType } from '../../../SvgIcon/svg-icon-type.mjs';
import useEventStopPropagation from '../../../../helpers/hooks/useEventStopPropagation.mjs';
import { withSlotScopeClasses } from '../../../../helpers/scoped-slots.mjs';
import { forwardRef } from 'react';
import { tv } from 'tailwind-variants';

const editorTagBadgeRecipe = tv({
  slots: withSlotScopeClasses("editor-tag-badge", {
    root: [
      "group relative flex flex-row flex-nowrap items-center justify-start",
      "max-w-full min-h-5 gap-0.5 px-1.5 py-0.5",
      "border border-solid rounded-[0.5rem]",
      "text-xs leading-4 font-medium select-none",
      "[&>.truncate]:leading-normal",
      "hover:cursor-pointer",
      // Disabled state when an ancestor has `aria-disabled="true"` (e.g. a
      // disabled DataBuilder field). Mirrors the `disabled` variant below
      // without requiring the recipe consumer to thread the prop down.
      "aria-disabled-ancestor:pointer-events-none aria-disabled-ancestor:bg-bg-emphasized aria-disabled-ancestor:border-bg-disabled aria-disabled-ancestor:text-fg-default aria-disabled-ancestor:cursor-[initial]"
    ],
    icon: "h-3 w-3 min-w-3 stroke-2",
    // Background gradient is set per-variant as a full `bg-[linear-gradient(...)]`
    // arbitrary value (not `bg-gradient-to-r` + `from-/via-/to-`). The TW3
    // utility chain sets `--tw-gradient-from/via/to` to values like
    // "<color> var(--tw-gradient-from-position)", which is invalid against
    // TW4's `@property --tw-gradient-from { syntax: "<color>" }` declaration
    // when the package runs inside a TW4 host (console, ui, screens) — the
    // registered initial-value (#0000) clobbers the intended color.
    "close-button-container": [
      "hidden absolute right-1 stroke-2 justify-end items-center",
      "rounded-tr-[0.375rem] rounded-br-[0.375rem] w-7 max-w-1/2 h-full",
      "group-hover:flex",
      "selected-ancestor:flex tag-dropdown-open-ancestor:flex"
    ],
    "close-button": [
      "appearance-none border-0 bg-transparent p-0 text-current",
      "rounded outline-none",
      "focus-visible:[&_svg]:stroke-3",
      "[&_svg]:h-3 [&_svg]:w-3 hover:[&_svg]:stroke-3"
    ]
  }),
  variants: {
    variant: {
      "static-value": {
        root: [
          "bg-gray-3 border-gray-4 text-fg-default",
          "hover:!bg-gray-4 hover:!border-gray-5",
          "active:bg-gray-5 active:border-gray-6",
          "selected-ancestor:bg-gray-5 selected-ancestor:border-gray-6",
          "tag-dropdown-open-ancestor:bg-gray-5 tag-dropdown-open-ancestor:border-gray-6"
        ],
        "close-button-container": [
          "group-hover:bg-[linear-gradient(to_right,transparent,var(--iap-colors-gray-4)_50%)]",
          "selected-ancestor:bg-[linear-gradient(to_right,transparent,var(--iap-colors-gray-5)_50%)]",
          "tag-dropdown-open-ancestor:bg-[linear-gradient(to_right,transparent,var(--iap-colors-gray-5)_50%)]"
        ],
        "close-button": "focus-visible:bg-gray-6"
      },
      formula: {
        root: [
          "bg-indigo-3 border-indigo-4 text-indigo-12",
          "hover:!bg-indigo-4 hover:!border-indigo-5",
          "active:bg-indigo-5 active:border-indigo-6",
          "selected-ancestor:bg-indigo-5 selected-ancestor:border-indigo-6",
          "tag-dropdown-open-ancestor:bg-indigo-5 tag-dropdown-open-ancestor:border-indigo-6"
        ],
        "close-button-container": [
          "group-hover:bg-[linear-gradient(to_right,transparent,var(--iap-colors-indigo-4)_50%)]",
          "selected-ancestor:bg-[linear-gradient(to_right,transparent,var(--iap-colors-indigo-5)_50%)]",
          "tag-dropdown-open-ancestor:bg-[linear-gradient(to_right,transparent,var(--iap-colors-indigo-5)_50%)]"
        ],
        "close-button": "focus-visible:bg-indigo-6"
      },
      value: {
        root: [
          "bg-blue-3 border-blue-4 text-blue-12",
          "hover:!bg-blue-4 hover:!border-blue-5",
          "active:bg-blue-5 active:border-blue-6",
          "selected-ancestor:bg-blue-5 selected-ancestor:border-blue-6",
          "tag-dropdown-open-ancestor:bg-blue-5 tag-dropdown-open-ancestor:border-blue-6"
        ],
        "close-button-container": [
          "group-hover:bg-[linear-gradient(to_right,transparent,var(--iap-colors-blue-4)_50%)]",
          "selected-ancestor:bg-[linear-gradient(to_right,transparent,var(--iap-colors-blue-5)_50%)]",
          "tag-dropdown-open-ancestor:bg-[linear-gradient(to_right,transparent,var(--iap-colors-blue-5)_50%)]"
        ],
        "close-button": "focus-visible:bg-blue-6"
      },
      error: {
        root: [
          "bg-tomato-3 border-tomato-4 text-tomato-10",
          "hover:!bg-tomato-4 hover:!border-tomato-5",
          "active:bg-tomato-5 active:border-tomato-6",
          "selected-ancestor:bg-tomato-5 selected-ancestor:border-tomato-6",
          "tag-dropdown-open-ancestor:bg-tomato-5 tag-dropdown-open-ancestor:border-tomato-6"
        ],
        "close-button-container": [
          "group-hover:bg-[linear-gradient(to_right,transparent,var(--iap-colors-tomato-4)_50%)]",
          "selected-ancestor:bg-[linear-gradient(to_right,transparent,var(--iap-colors-tomato-5)_50%)]",
          "tag-dropdown-open-ancestor:bg-[linear-gradient(to_right,transparent,var(--iap-colors-tomato-5)_50%)]"
        ],
        "close-button": "focus-visible:bg-tomato-6"
      },
      ghost: {
        root: "bg-transparent border-transparent text-neutral-12",
        "close-button": "hidden"
      }
    },
    disabled: {
      true: {
        root: "pointer-events-none bg-bg-emphasized border-bg-disabled text-fg-default cursor-[initial]"
      }
    }
  },
  defaultVariants: {
    variant: "value"
  }
});
const EditorTagBadge = forwardRef(({ children, className, variant, disabled, onDelete, ...rest }, ref) => {
  const styles = editorTagBadgeRecipe({ variant, disabled });
  const handleOnClick = useEventStopPropagation(() => onDelete?.());
  return /* @__PURE__ */ jsxs("div", { className: styles.root({ className }), ...rest, ref, children: [
    children,
    /* @__PURE__ */ jsx("div", { className: styles["close-button-container"](), children: /* @__PURE__ */ jsx("button", { className: styles["close-button"](), disabled, "aria-label": "Delete", onClick: handleOnClick, children: /* @__PURE__ */ jsx(SvgIcon, { type: SvgIconType.Close, className: styles.icon() }) }) })
  ] });
});
EditorTagBadge.displayName = "EditorTagBadge";
function EditorTagBadgeIcon({ type, className }) {
  const styles = editorTagBadgeRecipe();
  return /* @__PURE__ */ jsx(SvgIcon, { type, className: styles.icon({ className }) });
}

export { EditorTagBadge, EditorTagBadgeIcon };
